import type { AxfoodProductDetailsViewModel } from '@occ/api-client';
import { useInView } from 'react-intersection-observer';
import { ReactNode, isValidElement, cloneElement, useEffect, useRef } from 'react';

export interface Props {
  onInView: (product?: AxfoodProductDetailsViewModel, index?: number) => void;
  index?: number;
  product?: AxfoodProductDetailsViewModel;
  children?: ReactNode;
}

const TrackedComponent = ({ onInView, index, children, product }: Props) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    // Fire when 50% of the component is visible
    threshold: 0.5,
  });

  // Guard to ensure tracking only happens once
  // even if React re-renders or effects re-run.
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!inView) return;
    if (hasTrackedRef.current) return;

    hasTrackedRef.current = true;
    onInView(product, index);
  }, [inView, onInView, product, index]);

  // If children is a single valid React element, clone it and attach the trackedComponentRef.
  if (isValidElement(children)) {
    // `ref` here is a callback ref (function) from react-intersection-observer, not a RefObject.
    // Passing it as a prop doesn't read .current — children attach it to their DOM node.
    // A wrapper <div> would break CSS grid layouts where TrackedComponent is used.
    // eslint-disable-next-line react-hooks/refs
    return cloneElement(children, { trackedComponentRef: ref } as { trackedComponentRef: typeof ref });
  }

  // If no children are provided (or they aren't a single element),
  // render a div to attach the ref to.
  return <div ref={ref}>{children}</div>;
};

export default TrackedComponent;
